import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { authService } from '@/services/authService'

// The one place a Synstream session ends.
//
// Only the console holds the refresh token, and the logout API needs it, so a
// sign-out started anywhere else has to come through here: the dashboard and the
// pipeline editor send the browser to /logout instead of ending the session on
// their own. What they could do alone -- forget their own copy of the token --
// leaves the shared session alive on the console and in the cookie.
//
// Rendering nothing is deliberate. This route is a step on the way out, and a
// flash of layout on the way to the login form reads as a failed sign-out.
export default function LogoutPage() {
  const [searchParams] = useSearchParams()
  const done = useRef(false)

  useEffect(() => {
    // StrictMode mounts effects twice in development; sign out once.
    if (done.current) return
    done.current = true

    const back = searchParams.get('redirect')
    // Always land on the login form, and carry the caller's page so signing in
    // again returns there. A full page load, because the session that was just
    // dropped may be cached in more places than this app's own state.
    const query = new URLSearchParams()
    if (back) query.set('redirect', back)
    // A session that timed out has to say so on the login form, or signing in
    // again looks like the console threw the person out for no reason.
    const reason = searchParams.get('reason')
    if (reason === 'idle' || reason === 'expired') query.set('reason', reason)
    const next = query.toString() ? `/login?${query.toString()}` : '/login'

    // clearAuth() runs in authService's finally, so a failed API call still ends
    // the local session; the redirect happens either way for the same reason.
    authService.logout().finally(() => window.location.assign(next))
  }, [searchParams])

  return null
}
